import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/cn.ts'
import { BorderTrail } from '@/components/Motion/BorderTrail'

const buttonVariants = cva(
    'outline-none shrink-0 ' +
    'flex items-center justify-center box-border text-center text-body-md ' +
    'disabled:text-disabled disabled:bg-overlay-1 disabled:hover:text-disabled disabled:hover:bg-overlay-1 disabled:opacity-50 ' +
    'disabled:active:scale-100 disabled:cursor-not-allowed transition-200 cursor-pointer',
    {
        variants: {
            variant: {
                gradient: 'text-invert bg-brand-gradient hover:bg-brand-gradient-hover border border-solid border-stroke-button-primary',
                primary: 'text-invert bg-brand-primary hover:bg-brand-primary-hover border border-solid border-stroke-button-primary',
                primaryOrange: 'text-invert bg-orange hover:bg-orange-hover border border-solid border-stroke-button-primary',
                primaryRed: 'text-invert bg-red hover:bg-red-hover border border-solid border-stroke-button-primary',
                primaryPink: 'text-invert bg-pink hover:bg-pink-hover border border-solid border-stroke-button-primary',
                secondary: 'text-primary bg-overlay-1 hover:bg-overlay-2',
                secondaryOrange: 'text-orange bg-orange/10 hover:bg-orange-hover/20 bg-orange/10',
                secondaryRed: 'text-red bg-red/10 hover:bg-red-hover/20 bg-red/10',
                secondaryPink: 'text-pink bg-pink/10 hover:bg-pink-hover/20 bg-pink/10',
                secondaryBrand: 'text-brand-primary hover:bg-brand-primary-hover/20 bg-brand-primary/10',
                tertiaryAI: 'group relative text-brand-primary bor-2 hover:bg-brand-primary-hover/5 hover:border-brand-primary/20',
                tertiary: 'text-primary hover:bg-overlay-1',
                tertiaryBorder: 'text-primary bor-2 hover:bg-overlay-1',
                tertiaryBrand: 'text-brand-primary hover:bg-brand-primary/10',
                tertiaryBrandBorder: 'text-brand-primary bor-2 border-brand-primary hover:bg-brand-primary/10',
                tertiaryOrange: 'text-orange hover:bg-orange-hover/10',
                tertiaryRed: 'text-red hover:bg-red-hover/10',
                tertiaryPink: 'text-pink hover:bg-pink-hover/10',
                iconOnly: 'text-primary hover:bg-overlay-1'
            },
            size: {
                sm: 'h-6 px-2 gap-1 rounded-2 active:scale-96',
                md: 'h-8 px-3.5 gap-1.5 rounded-2.5 active:scale-96',
                lg: 'h-10 px-5 gap-2 rounded-3 active:scale-96',
                lgx: 'h-12 px-6.5 gap-3.5 rounded-3.5 active:scale-96',
                smIcon: 'size-6 rounded-2 active:scale-90',
                mdIcon: 'size-8 rounded-2.5 active:scale-90',
                lgIcon: 'size-10 rounded-3 active:scale-90'
            }
        },
        defaultVariants: {
            variant: 'tertiary',
            size: 'md',
        }
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, children, disabled, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button'
        const isIconSize = size === 'smIcon' || size === 'mdIcon' || size === 'lgIcon'
        const isTertiaryAI = variant === 'tertiaryAI'
        const borderSize = isIconSize ? 30 : 40

        return (
            <Comp
                className={cn(
                    !isIconSize && 'min-w-80px',
                    buttonVariants({ variant, size, className }),
                    
                )}
                ref={ref}
                disabled={disabled}
                {...props}
            >
                {isTertiaryAI && (
                    <BorderTrail
                        size={borderSize}
                        duration={5}
                        className="bg-brand-gradient opacity-50 group-hover:opacity-100"
                    />
                )}
                {children}
            </Comp>
        )
    }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
